import React, { useEffect, useState } from "react";
import { Category, CloudUpload, Polyline } from "@mui/icons-material";
import { Box, Button, Divider, Typography } from "@mui/material";

import { inputLayerGroups } from "fond/layers";
import { useAppSelector } from "fond/utils/hooks";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import { useAreaSelect } from "./polygon/useAreaSelect";

import { Alert, AlertTitle } from "./InputDataPanel.styles";

const AreaSelect: React.FC = () => {
  const { open, goBack } = useStackedNavigationContext();
  const [isReplacing, setIsReplacing] = useState(false);

  const { activeLayerGroupId } = useAppSelector((state) => state.project);
  const { hasData, uploadedArea, beginPolygonSelect, selectExistingPolygon, selectNewPolygon, selectPolygonUpload, close } =
    useAreaSelect(activeLayerGroupId);

  useEffect(() => {
    if (!hasData) {
      beginPolygonSelect();
    }
  }, [beginPolygonSelect, hasData]);

  const handleBack = () => {
    close();
    goBack();
  };

  const onDrawPolygonButtonClick = () => {
    selectNewPolygon();
    open("draw-polygon");
  };

  const onUseExistingPolygon = () => {
    selectExistingPolygon();
    open("use-existing-polygon");
  };

  const onUploadPolygon = () => {
    selectPolygonUpload();
    open("draw-polygon");
  };

  const onReplace = () => {
    setIsReplacing(true);
    beginPolygonSelect();
  };

  if (!isReplacing && hasData) {
    return (
      <>
        <StackedNavigationHeader onBackButtonClick={handleBack} title="Area Select" />
        <Box px={1} data-testid="replace-data-alert-box">
          <Typography fontWeight="500" mb={2}>
            {inputLayerGroups[activeLayerGroupId].name}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle data-testid="replace-data-header">Replace data</AlertTitle>
            <Typography variant="body3" component="p" mb={1}>
              You have already uploaded data for this layer. Proceeding with Area Select will replace it.
            </Typography>
            <Typography variant="body3" component="p">
              Are you sure you want to do this?
            </Typography>
          </Alert>
          <Divider />
          <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
            <Button color="primary" size="small" onClick={handleBack} data-testid="cancel-replace-button">
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={onReplace} sx={{ ml: 1, px: 2 }} data-testid="replace-button">
              Replace
            </Button>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Area Select" />
      <Box px={1} data-testid="polygon-select-panel">
        <Typography fontWeight="500">Ways to select an area</Typography>
        {uploadedArea && (
          <StackedNavigationButton
            data-testid="use-existing-polygon-button"
            icon={<Category />}
            title="Use existing polygon"
            subtitle="Reuse the existing polygon area"
            onClick={onUseExistingPolygon}
          />
        )}
        <StackedNavigationButton
          data-testid="draw-new-polygon-button"
          icon={<Polyline />}
          title="Draw polygon"
          subtitle="Draw a new polygon on the map"
          onClick={onDrawPolygonButtonClick}
        />
        <StackedNavigationButton
          data-testid="upload-polygon-button"
          icon={<CloudUpload />}
          title="Upload"
          subtitle="Upload a polygon area"
          onClick={onUploadPolygon}
        />
      </Box>
    </>
  );
};

export default AreaSelect;
